import {
  Check,
  Fire,
  HourglassMedium,
  PencilSimple,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react';
import type { PersonColor } from '@shh/design-tokens';
import { Button as AriaButton, Checkbox } from 'react-aria-components';
import { Icon } from '../atoms/Icon';
import { cardSurface, pressable } from '../lib/card';
import { cn } from '../lib/cn';
import { useLongPress } from '../lib/useLongPress';
import { personInk, personSoft } from '../lib/person';
import { PointsPill } from './PointsPill';
import { uiText } from '../i18n';

export type TaskStatus = 'open' | 'done' | 'pending';

export interface TaskCardProps {
  title: string;
  icon: PhosphorIcon;
  points?: number;
  status: TaskStatus;
  onStatusChange?: (done: boolean) => void;
  /** Optional hint under the title, e.g. "bis 18 Uhr". Hidden in the picture profile. */
  hint?: string;
  /** Days in a row ("🔥 5 Tage am Stück") – shown from 2 on. */
  streak?: number;
  isDisabled?: boolean;
  /** Colour of the icon tile – gives lists colour accents; usually the task's category or owner. */
  accent?: PersonColor;
  /** Spoken unit of the points, e.g. "Sterne". */
  pointsUnit?: string;
  /** compact: dashboard lists – smaller card, same touch target and semantics. */
  density?: 'regular' | 'compact';
  /**
   * "Bearbeiten · Löschen" (1.28): a long press on touch screens, a pencil on a PC (on hover and
   * for the keyboard – never hover only). Without it the card only ticks.
   */
  onEdit?: () => void;
  /** Spoken name of the pencil, e.g. "Zähne putzen bearbeiten". */
  editLabel?: string;
  className?: string;
}

/**
 * A task as a large, fully tappable card. Semantically a checkbox, so screen readers announce
 * "Zähne putzen, Kontrollkästchen, aktiviert" and keyboard users toggle it with Space.
 */
export function TaskCard({
  title,
  icon,
  points,
  status,
  onStatusChange,
  hint,
  streak,
  isDisabled,
  accent = 'lagoon',
  pointsUnit,
  density = 'regular',
  onEdit,
  editLabel,
  className,
}: TaskCardProps) {
  const compact = density === 'compact';
  const isSelected = status !== 'open';
  const hold = useLongPress(onEdit);
  const card = (
    <Checkbox
      isSelected={isSelected}
      // the press that ends a long press opened the menu – it must not also tick
      onChange={(v) => !hold.consume() && onStatusChange?.(v)}
      isDisabled={isDisabled}
      className={cn(
        'group flex w-full cursor-pointer items-center rounded-card',
        compact
          ? 'min-h-touch-min gap-gap-md p-inset-xs'
          : 'min-h-touch-target gap-gap-lg p-inset-sm',
        cardSurface,
        pressable,
        'outline-none data-focus-visible:outline-3 data-focus-visible:outline-offset-2 data-focus-visible:outline-border-focus',
        // A done task stays "pressed in" – in the block look it sits flat in its own shadow.
        'data-selected:bg-feedback-success-surface data-selected:shadow-none',
        'data-selected:translate-x-(--shh-card-press-offset) data-selected:translate-y-(--shh-card-press-offset)',
        'data-disabled:cursor-not-allowed',
        onEdit && 'select-none [-webkit-touch-callout:none]',
        !onEdit && className,
      )}
    >
      <span
        className={cn(
          'grid shrink-0 place-items-center rounded-control',
          compact ? 'size-icon-xl' : 'size-touch-min',
          personSoft[accent],
          'group-data-selected:bg-surface-raised',
        )}
      >
        <Icon
          icon={icon}
          size={compact ? 'md' : 'lg'}
          className={cn(personInk[accent], 'group-data-selected:text-feedback-success')}
        />
      </span>

      <span className="flex min-w-0 flex-1 flex-col">
        <span
          className={cn(
            // German compounds ("Schulranzen") must hyphenate instead of overflowing at large kid sizes.
            compact ? 'text-md font-semibold' : 'text-lg font-semibold',
            'text-text-primary hyphens-auto break-words',
            'group-data-selected:text-text-secondary group-data-selected:line-through group-data-selected:decoration-2',
          )}
        >
          {title}
        </span>
        {status === 'pending' ? (
          <span className="flex items-center gap-gap-xs text-sm text-text-secondary">
            <Icon icon={HourglassMedium} size="sm" />
            {uiText().waiting}
          </span>
        ) : (
          hint && <span className="text-sm text-text-secondary">{hint}</span>
        )}
        {streak !== undefined && streak >= 2 && (
          <span className="flex items-center gap-1 text-sm font-bold text-text-primary">
            {/* the flame carries the colour; text in the colour would be too faint to read */}
            <Icon icon={Fire} size="sm" weight="fill" className="text-reward-streak" />
            {uiText().streak(streak >= 60 ? '60+' : String(streak))}
          </span>
        )}
      </span>

      {points !== undefined && points > 0 && (
        <PointsPill points={points} signed unit={pointsUnit} />
      )}

      <span
        aria-hidden
        className={cn(
          'grid size-icon-xl shrink-0 place-items-center rounded-avatar border-3 border-border-strong',
          'group-data-selected:animate-pop group-data-selected:border-feedback-success group-data-selected:bg-feedback-success group-data-selected:text-text-on-action',
        )}
      >
        <Icon
          icon={Check}
          weight="bold"
          size="md"
          className="opacity-0 group-data-selected:opacity-100"
        />
      </span>
    </Checkbox>
  );
  if (!onEdit) return card;
  return (
    <div className={cn('group/edit relative', className)} {...hold.handlers}>
      {card}
      {/* hidden until hover or keyboard focus; on touch screens a long press does the same */}
      <AriaButton
        aria-label={editLabel ?? uiText().edit}
        onPress={onEdit}
        className={cn(
          'absolute top-0 right-0 grid size-icon-xl -translate-y-1/3 translate-x-1/3 cursor-pointer place-items-center rounded-avatar',
          'border border-border-default bg-surface-raised text-text-secondary outline-none',
          // Tailwind's hover only exists on devices that can hover: a finger never hits it unseen
          'pointer-events-none opacity-0 group-hover/edit:pointer-events-auto group-hover/edit:opacity-100',
          'data-focus-visible:pointer-events-auto data-focus-visible:opacity-100',
          'data-focus-visible:outline-3 data-focus-visible:outline-border-focus data-hovered:text-text-primary',
        )}
      >
        <Icon icon={PencilSimple} size="sm" />
      </AriaButton>
    </div>
  );
}
